import { useMemo, useEffect } from "react";
import { Location, useLocation, useNavigate } from 'react-router-dom';
import { Constants, helpers } from "@shared";
import currency from "currency.js";
import { useForm, validators, useAllowedPaymentTypes } from "@hooks";
import { v4 as uuidv4 } from 'uuid';
import { Button, IconButton, HelpFooter, MoneyInput, TextField } from "@common";
import { PencilIcon } from "@icons";
import { PaymentMethodBadge } from "./payment-method-badge";
import { useSessionContext } from '@contexts';
import type { CurrentBill } from '@app-types';
import { PostHogEvents, capturePosthogEvent } from '@analytics';

interface LocationState {
    currentBill: CurrentBill;
    account: {
        idPayor: number;
        idAccount: number;
    };
}

interface FormValues {
    amount: string;
}

const MakePayment = () => {

    const navigate = useNavigate();
    const location = useLocation() as Location<LocationState>;
    const { allowedPaymentTypes } = useAllowedPaymentTypes();
    const { accounts } = useSessionContext();

    const {
        state: {
            currentBill,
            account: { idPayor, idAccount }
        }
    } = location;

    const account = accounts.find(acc => acc.idAccount === idAccount);
    const defaultPaymentMethod = account?.paymentPreferences?.defaultPaymentMethod;

    // I think it's pretty unlikely that defaultPaymentMethod will be "not set"
    // if the user is accessing this page, but it seems theoretically possible
    // if the user navigated here via an unexpected path, or if there's a bug
    // somewhere.  So this page should prevent payments in that case, and the
    // user should be able to go to the settings page to set it up.  Comment-out
    // the line above and un-comment the line below to test that state.
    //
    // const defaultPaymentMethod = undefined;

    const { dueDate, invitations } = currentBill;
    const month = helpers.monthName(dueDate);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const submit = () => {
        const amount = currency(values.amount).value;
        capturePosthogEvent(PostHogEvents.PAYMENT_INITIATED, {
            amount,
            paymentType: defaultPaymentMethod?.idPaymentType,
            balance: total.value
        });
        helpers.navigateNext(location, navigate, { amount }, {
            amount,
            idempotencyKey: uuidv4()
        });
    };

    const total = useMemo(() => {
        const invitation = invitations.find(i => i.idPayor === idPayor);
        if (!invitation) return currency(0); // For TS compiler; should never happen
        return currency(invitation.balance);
    }, [currentBill]);

    const minPayment = currency(Constants.System.MIN_PAYMENT_AMOUNT, { format: helpers.formatConditionalCents });
    const maxPayment = currency(total, { format: helpers.formatConditionalCents });

    const initialState: FormValues = { amount: `` };
    const validations = {
        amount: (val: string) => {
            // Check if value is empty or not a valid number
            if (!validators.hasAmount(val)) {
                return { amount: `Please enter an amount.` };
            }

            const amount = currency(val);

            // Check if amount is below minimum
            if (amount.value < minPayment.value) {
                return { amount: `Amount must be at least ${minPayment.format()}.` };
            }

            // Check if amount exceeds balance
            if (amount.value > maxPayment.value) {
                return { amount: `Amount cannot exceed your balance of ${maxPayment.format()}.` };
            }

            // Check if remaining balance would be less than minimum (and not zero)
            const remainingBalance = maxPayment.subtract(amount);
            if (remainingBalance.intValue !== 0 && remainingBalance.value < minPayment.value) {
                return { amount: `Remaining balance must be at least ${minPayment.format()}, or pay in full.` };
            }

            return true;
        }
    };
    const { values, errors, submitHandler, customUpdate } = useForm(initialState, {}, validations, submit);

    const defaultPaymentMethodAllowed = defaultPaymentMethod?.idPaymentType !== undefined
        && allowedPaymentTypes.includes(defaultPaymentMethod.idPaymentType);

    const isFormSubmittable = values.amount && values.amount.trim().length > 0 && defaultPaymentMethodAllowed;

    const handleEditPaymentMethod = () => {
        navigate(`/payment-method-settings`);
    };

    return (
        <form method="POST" onSubmit={submitHandler} className="flex flex-col w-full">
            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 w-full">
                <h2 className="font-medium text-[20px] leading-6 md:text-[28px] md:leading-[40px] text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">
                    {month} Balance
                </h2>
            </div>

            {/* Spacing after header */}
            <div className="h-16" />

            {/* Balance Section */}
            <div className="flex flex-col gap-6 items-center w-full">
                {/* Large Balance Display */}
                <p className="font-medium text-[48px] leading-[56px] text-neutral-950 tracking-[0.48px] text-center">
                    {total.format()}
                </p>

                {/* Amount Input Section */}
                <div className="flex flex-col gap-4 w-full md:w-[420px]">
                    <MoneyInput
                        as={TextField}
                        name="amount"
                        placeholder={`Enter amount to pay (${minPayment.format()} to ${maxPayment.format()})`}
                        value={values.amount}
                        onValueChange={(value) => customUpdate({ amount: value })}
                        error={errors.amount}
                        containerClassName="text-center"
                        className="text-center"
                    />

                    {/* Payment Method Display */}
                    <div className="flex items-center justify-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-sm leading-5 text-neutral-950 tracking-[0.14px]">Payment Method</span>
                            <div className="flex items-center gap-1">
                                <PaymentMethodBadge
                                    paymentType={defaultPaymentMethod?.idPaymentType}
                                    sourceName={defaultPaymentMethod?.sourceName}
                                />
                                <span className="text-sm leading-5 text-neutral-700 tracking-[0.14px]">
                                    {defaultPaymentMethod?.last4 || `Not set`}
                                </span>
                            </div>
                        </div>
                        <IconButton
                            variant="tertiary"
                            size="xs"
                            onClick={handleEditPaymentMethod}
                            aria-label="Edit payment method"
                        >
                            <PencilIcon />
                        </IconButton>
                    </div>
                </div>

                {/* Make a Payment Button */}
                <div className="w-full md:w-[420px]">
                    <Button
                        type="submit"
                        variant="primary"
                        size="xl"
                        disabled={!isFormSubmittable}
                        className="w-full"
                    >
                        Make a Payment
                    </Button>
                </div>
            </div>

            {/* Flexible spacing before footer - pushes footer to bottom */}
            <div className="flex-1 min-h-20" />

            {/* Help Footer */}
            <HelpFooter />
        </form>
    );
};

export default MakePayment;
